<template>
  <app-custom-dialog
    :value="value"
    width="720px"
    :footer="true"
    title="cancel_items_refund"
    cancelText="close"
    submitText="confirm_cancel"
    :loading="loading"
    :disableSubmit="!hasSelection"
    @input="$emit('input', $event)"
    @submit="submit"
  >
    <div slot="activator">
      <!-- Which bill this is and how it was paid -->
      <div v-if="ticket" class="bill-summary px-3 py-2 mb-3 text-body-2">
        <span class="font-weight-bold">{{ ticket.code }}</span>
        · {{ Number(ticket.total_price || 0) | NumberFormat }} LAK ·
        {{ $t("paid_with") }}: {{ $t(ticket.type || "cash") }}
        {{ paymentCurrency().code }}
      </div>

      <div class="d-flex align-center justify-space-between mb-2">
        <span class="text-body-2">{{ $t("select_items_to_cancel") }}</span>
        <v-btn
          v-if="canCancelItems && lines.length"
          small
          text
          color="primary"
          class="text-none"
          @click="toggleSelectAll"
        >
          <v-icon left small>
            {{ allSelected ? "fal fa-times-square" : "fal fa-check-square" }}
          </v-icon>
          {{ allSelected ? $t("clear_selection") : $t("select_all") }}
        </v-btn>
      </div>

      <div class="cancel-items mb-3">
        <div
          v-for="line in lines"
          :key="line.id"
          class="cancel-item d-flex align-center px-3 py-2"
        >
          <div class="flex-grow-1 mr-3">
            <div class="font-weight-medium">{{ lineName(line) }}</div>
            <div class="text-caption text--secondary">
              {{ unitPrice(line) | NumberFormat }} x {{ line.qty }}
              <span v-if="Number(line.cancelled_qty) > 0">
                ·
                {{
                  $t("cancelled_qty_by", {
                    qty: line.cancelled_qty,
                    names: cancelledBy(line) || "-",
                  })
                }}
              </span>
            </div>
          </div>
          <div class="d-flex align-center qty-stepper">
            <v-btn
              x-small
              fab
              depressed
              :disabled="!canCancelItems || qtyOf(line) <= 0"
              :aria-label="$t('decrease')"
              @click="setQty(line, qtyOf(line) - 1)"
            >
              <v-icon x-small>fal fa-minus</v-icon>
            </v-btn>
            <v-text-field
              :key="`qty-${line.id}-${inputKeys[line.id] || 0}`"
              :value="qtyOf(line)"
              type="number"
              min="0"
              :max="line.qty"
              dense
              outlined
              hide-details
              :readonly="!canCancelItems"
              class="mx-2 qty-input"
              :aria-label="$t('cancel_qty')"
              @input="setQty(line, $event)"
              @blur="redrawQty(line)"
            />
            <v-btn
              x-small
              fab
              depressed
              :disabled="!canCancelItems || qtyOf(line) >= Number(line.qty)"
              :aria-label="$t('increase')"
              @click="setQty(line, qtyOf(line) + 1)"
            >
              <v-icon x-small>fal fa-plus</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Everything left on the bill is selected: the bill itself goes. -->
      <v-alert
        v-if="allSelected"
        dense
        outlined
        type="warning"
        class="mb-3 text-body-2 whole-bill-alert"
      >
        {{ $t("all_items_selected_whole_bill") }}
      </v-alert>

      <div class="d-flex justify-space-between text-body-2">
        <span>{{ $t("cancelled_items_amount") }}</span>
        <span>{{ selectedAmount | NumberFormat }} LAK</span>
      </div>
      <!-- What the cashier actually hands back, in the currency paid with -->
      <div
        v-if="canCancelItems"
        class="d-flex justify-space-between text-body-1 font-weight-bold mb-4"
      >
        <span>{{ $t("estimated_refund") }}</span>
        <span>{{ formatRefund(estimatedRefund) }}</span>
      </div>
      <div v-else class="mb-4" />

      <TextArea
        id="bill-report-remark"
        label="cancel_reason_required"
        :aria-label="$t('noted')"
        :errorMessages="remarkError"
        v-model="remark"
      />

      <CancelHistory
        class="mt-4"
        :cancellations="(ticket && ticket.cancellations) || []"
      />
    </div>
  </app-custom-dialog>
</template>

<script>
import TextArea from "@/components/global/TextArea";
import CancelHistory from "@/components/reports/CancelHistory";
import { myMixin } from "@/mixin/main";
import { ticketPaymentCurrency } from "@/mixin/ticketPaymentCurrency";
import { toast, confirm } from "@/controllers/alert";

/**
 * Cancel dialog of the bill report (QRIPO-543): one list of the bill's items.
 * Pick some units of some lines to cancel them; pick everything and the whole
 * bill is cancelled, which cancel_ticket_items does itself once no line is
 * left. A bill that is not sold yet (done/overdue) can only go as a whole, so
 * its list is preselected and read-only and it goes through cancel_ticket.
 *
 * The backend works out every figure; this dialog only names lines and
 * quantities. The refund it reports back is in kip and is shown here in the
 * currency the customer paid with, so the cashier hands back the same money.
 */
export default {
  name: "CancelTicketDialog",
  mixins: [myMixin, ticketPaymentCurrency],
  components: { TextArea, CancelHistory },
  props: {
    value: { type: Boolean, default: false },
    // The bill as bill_detail/{id} returns it.
    ticket: { type: Object, default: null },
  },
  data: () => ({
    remark: "",
    qtyById: {},
    // Bumped to redraw a qty box whose typed value was clamped (typing 99
    // into a line of 2 keeps 2, but the field itself still showed 99).
    inputKeys: {},
    submitted: false,
    loading: false,
  }),
  computed: {
    canCancelItems() {
      return ["done", "overdue"].includes(this.ticket?.status);
    },
    // bill_detail already leaves cancelled lines out; the guard keeps it that way.
    lines() {
      return (this.ticket?.history || []).filter(
        (line) => line.status !== "cancel" && Number(line.qty) > 0
      );
    },
    selectedItems() {
      return this.lines
        .filter((line) => this.qtyOf(line) > 0)
        .map((line) => ({ history_id: line.id, qty: this.qtyOf(line) }));
    },
    hasSelection() {
      return this.selectedItems.length > 0;
    },
    allSelected() {
      return (
        this.lines.length > 0 &&
        this.lines.every((line) => this.qtyOf(line) === Number(line.qty))
      );
    },
    // Goods value only: the backend also takes the bill discount and VAT
    // into account, so the refund it reports can differ from this.
    selectedAmount() {
      return this.lines.reduce(
        (sum, line) => sum + this.unitPrice(line) * this.qtyOf(line),
        0
      );
    },
    /**
     * Refund before confirming, the way cancel_ticket_items works it out:
     * the discount and VAT move in proportion to the goods taken off, and
     * only what was paid beyond the new total goes back. The server's figure
     * after confirming is the real one; this only differs by rounding.
     */
    estimatedRefund() {
      const t = this.ticket || {};
      const totalBefore = Number(t.total_price || 0);
      const paidBefore = Math.min(Number(t.total_paid || 0), totalBefore);
      const saleBefore = Number(t.total_price_sale || 0);
      const taxableBefore = saleBefore - Number(t.total_discount || 0);
      if (!this.hasSelection || totalBefore <= 0 || taxableBefore <= 0) {
        return 0;
      }
      if (this.allSelected) return paidBefore;
      const saleAfter = Math.max(saleBefore - this.selectedAmount, 0);
      // The discount is prorated, so the taxable base shrinks by the same share.
      const totalAfter = (totalBefore * saleAfter) / saleBefore;
      const paidAfter = Math.min(paidBefore, totalAfter);
      return Math.max(Math.round((paidBefore - paidAfter) * 100) / 100, 0);
    },
    remarkError() {
      return this.submitted && !this.remark.trim()
        ? [this.$t("validation_required")]
        : [];
    },
  },
  watch: {
    value(open) {
      if (open) this.reset();
    },
  },
  methods: {
    reset() {
      this.remark = "";
      this.qtyById = {};
      this.submitted = false;
      // Not sold yet: the whole bill is the only thing that can go.
      if (!this.canCancelItems) this.selectAll();
    },
    selectAll() {
      this.lines.forEach((line) => this.setQty(line, line.qty));
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.qtyById = {};
      } else {
        this.selectAll();
      }
    },
    // Who took units off this line, from the bill's cancel history.
    cancelledBy(line) {
      const names = (this.ticket?.cancellations || [])
        .filter(
          (entry) =>
            entry.action !== "add" &&
            (entry.items || []).some((item) => item.history_id === line.id)
        )
        .map((entry) => entry.user_name)
        .filter(Boolean);
      return [...new Set(names)].join(", ");
    },
    redrawQty(line) {
      this.$set(this.inputKeys, line.id, (this.inputKeys[line.id] || 0) + 1);
    },
    qtyOf(line) {
      return Number(this.qtyById[line.id] || 0);
    },
    setQty(line, value) {
      const qty = Math.max(
        0,
        Math.min(Number(line.qty), parseInt(value, 10) || 0)
      );
      this.$set(this.qtyById, line.id, qty);
      if (String(value) !== String(qty) && value !== "") this.redrawQty(line);
    },
    unitPrice(line) {
      const qty = Number(line.qty) || 1;
      return Number(line.total_price || 0) / qty;
    },
    lineName(line) {
      const name = line.product?.name || "";
      // Laravel serialises the productVariant relation as product_variant.
      const variant = line.product_variant || line.productVariant;
      const options = Object.values(variant?.optionValues || {});
      if (!options.length && variant?.sku) options.push(variant.sku);
      return options.length ? `${name} (${options.join(", ")})` : name;
    },
    formatRefund(refundLak) {
      return this.formatInPaidCurrency(refundLak);
    },
    async submit() {
      this.submitted = true;
      if (!this.remark.trim() || !this.hasSelection) return;
      if (this.canCancelItems) {
        await this.cancelItems();
      } else {
        await this.cancelWholeBill();
      }
    },
    async cancelWholeBill() {
      this.loading = true;
      try {
        const { data } = await this.$http.post("cancel_ticket", {
          ticket_id: this.ticket.id,
          // cancel_ticket still requires a reason code; the dialog asks for
          // the reason as free text in the remark instead.
          return_type: "other",
          remark: this.remark.trim(),
        });
        if (data.status) {
          this.$emit("input", false);
          await toast(this, "cancel_ticket_success");
          this.$emit("cancelled", { ticket: null });
        }
      } catch (e) {
        // The http interceptor already shows the API error.
      } finally {
        this.loading = false;
      }
    },
    async cancelItems() {
      this.loading = true;
      let result = null;
      try {
        const { data } = await this.$http.post("cancel_ticket_items", {
          ticket_id: this.ticket.id,
          remark: this.remark.trim(),
          items: this.selectedItems,
        });
        if (data.status) result = data.data;
      } catch (e) {
        // The http interceptor already shows the API error.
      } finally {
        this.loading = false;
      }
      if (!result) return;

      this.$emit("input", false);
      this.$emit("cancelled", { ticket: result.ticket });

      const answer = await confirm({
        props: this,
        title:
          result.ticket?.status === "cancel"
            ? "cancel_ticket_success"
            : "cancel_ticket_items_success",
        text: "refund_amount_reprint",
        textParams: {
          amount: this.formatRefund(Number(result.refund_amount || 0)),
        },
        con_txt: "print",
        can_txt: "close",
      });
      if (answer?.isConfirmed) {
        this.$emit("print", result.ticket);
      }
    },
  },
};
</script>

<style scoped>
/* The orange border and icon flag it; orange words on white are too faint. */
.whole-bill-alert ::v-deep .v-alert__content {
  color: var(--app-text-primary, #333);
}

.bill-summary {
  border: 1px solid var(--app-border, #e0e0e0);
  border-radius: 8px;
}

.cancel-items {
  border: 1px solid var(--app-border, #e0e0e0);
  border-radius: 8px;
  max-height: 320px;
  overflow-y: auto;
}

.cancel-item + .cancel-item {
  border-top: 1px solid var(--app-border, #e0e0e0);
}

.qty-input {
  width: 72px;
  flex: 0 0 72px;
}

.qty-input ::v-deep input {
  text-align: center;
}
</style>
